import { useState } from 'react';
import { Contact } from '../types';
import { Pencil, Trash2 } from 'lucide-react';
import { formatPhoneNumber } from '../utils/phoneUtils';

interface ContactListProps {
  contacts: Contact[];
  loading: boolean;
  onEdit: (contact: Contact) => void;
  onDelete: (id: string) => void;
  selectedContactIds?: string[];
  onToggleSelect?: (id: string) => void;
  onSelectAll?: () => void;
  selectionMode?: boolean;
}

export function ContactList({
  contacts,
  loading,
  onEdit,
  onDelete,
  selectedContactIds = [],
  onToggleSelect,
  onSelectAll,
  selectionMode = false,
}: ContactListProps) {
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const handleDelete = (id: string) => {
    if (deleteConfirm === id) {
      onDelete(id);
      setDeleteConfirm(null);
    } else {
      setDeleteConfirm(id);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-6" role="status" aria-label="Carregando contatos">
        <div className="relative">
          <div className="w-12 h-12 border-4 border-blue-500/20 border-t-blue-500 rounded-full animate-spin"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-6 h-6 bg-blue-500/10 rounded-full animate-pulse"></div>
          </div>
        </div>
        <span className="text-gray-400 font-oswald uppercase tracking-[0.2em] font-bold text-xs">Carregando contatos...</span>
      </div>
    );
  }

  if (contacts.length === 0) {
    return (
      <div className="text-center py-20 bg-white/5 border border-dashed border-white/10 rounded-[2rem] mx-10 my-10">
        <div className="w-16 h-16 bg-white/5 rounded-full flex items-center justify-center mx-auto mb-4 border border-white/5">
          <span className="text-2xl">👤</span>
        </div>
        <p className="text-[10px] text-gray-500 font-bold uppercase tracking-[0.2em] font-oswald">Nenhum contato encontrado</p>
      </div>
    );
  }

  const allSelected = contacts.length > 0 && selectedContactIds.length === contacts.length;

  return (
    <div className="overflow-hidden">
      <table className="w-full divide-y divide-white/10" role="table">
        <thead className="bg-white/5 sticky top-0 z-10 backdrop-blur-md">
          <tr>
            {selectionMode && (
              <th className="px-4 py-6 text-left">
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={onSelectAll}
                  className="h-4 w-4 text-blue-500 bg-white/5 border-white/20 rounded cursor-pointer focus:ring-blue-500 focus:ring-offset-0"
                  aria-label="Selecionar todos os contatos"
                />
              </th>
            )}
            <th className="px-4 py-6 text-left text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald">
              Nome
            </th>
            <th className="px-4 py-6 text-left text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald">
              Telefone
            </th>
            <th className="px-4 py-6 text-left text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald">
              Categoria
            </th>
            <th className="px-4 py-6 text-left text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald">
              Atualizado em
            </th>
            <th className="px-4 py-6 text-right text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald">
              Ações
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-white/5">
          {contacts.map((contact) => {
            const isSelected = selectedContactIds.includes(contact.id);
            return (
              <tr key={contact.id} className={`hover:bg-white/[0.02] transition-colors group ${isSelected ? 'bg-blue-500/5' : ''}`}>
                {selectionMode && (
                  <td className="px-4 py-6">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => onToggleSelect?.(contact.id)}
                      className="h-4 w-4 text-blue-500 bg-white/5 border-white/20 rounded cursor-pointer focus:ring-blue-500 focus:ring-offset-0"
                      aria-label={`Selecionar contato ${contact.nome}`}
                    />
                  </td>
                )}
                <td className="px-4 py-6">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-600/20 to-blue-900/20 border border-blue-500/20 flex items-center justify-center text-blue-400 font-bold font-oswald text-sm">
                      {contact.nome.charAt(0).toUpperCase()}
                    </div>
                    <div className="text-sm font-bold text-white uppercase tracking-wide font-oswald truncate max-w-[120px]">
                      {contact.nome}
                    </div>
                  </div>
                </td>
                <td className="px-4 py-6 text-sm text-gray-400 font-medium">
                  {formatPhoneNumber(contact.telefone)}
                </td>
                <td className="px-4 py-6">
                  {contact.categoria ? (
                    <span 
                      className="inline-flex items-center px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-[0.2em] border shadow-sm shadow-black/20"
                      style={{
                        backgroundColor: `${contact.categoria.cor}15`,
                        color: contact.categoria.cor,
                        borderColor: `${contact.categoria.cor}30`
                      }}
                    >
                      {contact.categoria.nome}
                    </span>
                  ) : (
                    <span className="text-gray-600 text-[10px] font-bold uppercase tracking-[0.2em] font-oswald">-</span>
                  )}
                </td>
                <td className="px-4 py-6 text-sm text-gray-400 font-medium">
                  {formatDate(contact.atualizadoEm)}
                </td>
                <td className="px-4 py-6 text-right">
                  <div className="flex items-center justify-end gap-2">
                    <button
                      onClick={() => onEdit(contact)}
                      className="p-2 bg-white/5 text-blue-400 hover:bg-white/10 rounded-lg border border-white/10 transition-all"
                      title="Editar"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>

                    <button
                      onClick={() => handleDelete(contact.id)}
                      className={`p-2 bg-white/5 rounded-lg border border-white/10 transition-all ${
                        deleteConfirm === contact.id ? 'text-red-500 bg-red-500/10 border-red-500/20' : 'text-gray-400 hover:text-red-400 hover:bg-red-500/10'
                      }`}
                      title={deleteConfirm === contact.id ? 'Confirmar exclusão' : 'Excluir'}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}